import { bunja } from 'bunja';
import { atom } from 'jotai';

import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { SocketState } from '../../AbstractSocket';
import {
  IntervalSnapshotSocketClosedError,
  IntervalSnapshotTimeoutError,
} from '../../misc/errors/interval-snapshot-error';
import { r230522SocketBunja } from '../socket';
import type { IntervalData, WsV2Message } from '../types';
import { IntervalDataType, IntervalKey, intervalKeyToString } from './interval-key';
import { throwWhenTimeout } from './misc';

export type ToggleSocketIntervalDataFn = (
  symbol: string,
  interval: string,
  intervalDataType?: IntervalDataType,
) => void;
export type RequestIntervalSnapshotFn = (intervalKey: string, maxCount?: number) => Promise<IntervalData[]>;

const subscriptionsBunja = bunja(() => {
  const subscriptionsAtom = atom<string[]>([]);

  return {
    subscriptionsAtom,
  };
});
subscriptionsBunja.debugLabel = 'subscriptionsBunja';

let intervalSnapshotRequestCount = 0;
export const intervalSocketBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { r230522Socket } = bunja.use(r230522SocketBunja);
  const { subscriptionsAtom } = bunja.use(subscriptionsBunja);

  const intervalSocketStateAtom = atom<SocketState>('closed');
  intervalSocketStateAtom.debugLabel = `${r230522Socket.debugLabel} - state`;

  const isConnectedIntervalAtom = atom<boolean>((get) => get(intervalSocketStateAtom) === 'connected');
  isConnectedIntervalAtom.debugLabel = `${r230522Socket.debugLabel} - isConnected`;

  type IntervalSnapshotRequestHandler = (response?: IntervalData[], error?: Error) => void;
  const intervalSnapshotRequestTable = new Map<string, IntervalSnapshotRequestHandler>();
  function clearRequestTable() {
    intervalSnapshotRequestTable.forEach((handler) => handler(undefined, new IntervalSnapshotSocketClosedError()));
    intervalSnapshotRequestTable.clear();
  }

  const updateSubscriptions = (subscriptions: string[]) => {
    r230522Socket.send({ s: { intervalData: subscriptions } });
    store.set(subscriptionsAtom, subscriptions);
  };

  function onStateChanged(state: SocketState) {
    store.set(intervalSocketStateAtom, state);
    if (state === 'connected') {
      updateSubscriptions(store.get(subscriptionsAtom));
    }
  }

  const subscribeSocketIntervalData: ToggleSocketIntervalDataFn = (symbol, interval, intervalDataType = 'MID') => {
    const intervalKey = intervalKeyToString({ op: 'subscribe', symbol, interval, intervalDataType } as IntervalKey);
    const newSubscriptions = new Set(store.get(subscriptionsAtom));
    newSubscriptions.add(intervalKey);
    updateSubscriptions([...newSubscriptions]);
  };

  const unsubscribeSocketIntervalData: ToggleSocketIntervalDataFn = (symbol, interval, intervalDataType = 'MID') => {
    const intervalKey = intervalKeyToString({ op: 'subscribe', symbol, interval, intervalDataType } as IntervalKey);
    const newSubscriptions = new Set(store.get(subscriptionsAtom));
    newSubscriptions.delete(intervalKey);
    updateSubscriptions([...newSubscriptions]);
  };

  /**
   * 최신 봉 데이터를 최대 2개까지 가져옵니다.
   * context: https://prextech.slack.com/archives/C06D8R123HN/p1725605614425119
   */
  const requestIntervalSnapshot: RequestIntervalSnapshotFn = (intervalKey, maxCount) => {
    return Promise.race([
      throwWhenTimeout(5000, () => new IntervalSnapshotTimeoutError()),
      new Promise<IntervalData[]>((resolve, reject) => {
        const id = String((intervalSnapshotRequestCount += 1));
        r230522Socket.send({
          c: { id, req: { command: 'intervalSnapshot', intervals: [intervalKey], maxCount } },
        });
        intervalSnapshotRequestTable.set(id, (response, error) => {
          if (error) reject(error);
          else resolve(response || []);
        });
      }),
    ]);
  };

  function intervalSnapshotMessageHandler(message: unknown) {
    const { c } = message as WsV2Message;
    if (c) {
      const requestHandler = intervalSnapshotRequestTable.get(c.id || '');
      if (requestHandler) {
        const response = (Object.values(c.res) as IntervalData[][])[0] || [];
        requestHandler(response);
        intervalSnapshotRequestTable.delete(c.id || '');
      }
    }
  }

  function intervalErrorHandler(error: Error) {
    // oxlint-disable-next-line no-console
    console.error('interval socket error:', error);
  }

  bunja.effect(() => {
    r230522Socket.on('closed', clearRequestTable);
    r230522Socket.on('stateChanged', onStateChanged);
    r230522Socket.on('message', intervalSnapshotMessageHandler);
    r230522Socket.on('error', intervalErrorHandler);
    return () => {
      r230522Socket.off('closed', clearRequestTable);
      r230522Socket.off('stateChanged', onStateChanged);
      r230522Socket.off('message', intervalSnapshotMessageHandler);
      r230522Socket.off('error', intervalErrorHandler);
    };
  });

  return {
    r230522Socket,
    subscribeSocketIntervalData,
    unsubscribeSocketIntervalData,
    requestIntervalSnapshot,
    isConnectedIntervalAtom,
  };
});
intervalSocketBunja.debugLabel = 'intervalSocketBunja';
